/**
 * Axios API client with request/response interceptors
 */

import axios, { AxiosError } from 'axios'
import { msalInstance } from '@/core/auth/authConfig'
import { authEvents } from '@/core/auth/authEvents'
import { ApiError } from '@/shared/types/common.types'

const LOCAL_TOKEN_KEY = 'hrms_local_token'

// Track if we're currently handling a 401 to prevent multiple logout attempts
let isHandling401 = false

// Create axios instance
const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:5000/api',
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
})

// Request interceptor to add authentication token
apiClient.interceptors.request.use(
  async (config) => {
    // First, check for local authentication token
    const localToken = sessionStorage.getItem(LOCAL_TOKEN_KEY)
    if (localToken) {
      config.headers.Authorization = `Bearer ${localToken}`
      return config
    }

    // Otherwise, try Microsoft authentication
    const accounts = msalInstance.getAllAccounts()

    if (accounts.length > 0) {
      try {
        // Get ID token for backend authentication first (primary auth)
        const response = await msalInstance.acquireTokenSilent({
          scopes: ['User.Read', 'openid', 'profile', 'email'],
          account: accounts[0],
        })

        // Add ID token for authentication
        config.headers.Authorization = `Bearer ${response.idToken}`

        // Then try to get Graph API access token (optional, for manager sync)
        try {
          const graphResponse = await msalInstance.acquireTokenSilent({
            scopes: ['https://graph.microsoft.com/User.Read', 'https://graph.microsoft.com/User.ReadBasic.All'],
            account: accounts[0],
            forceRefresh: false,
          })
          config.headers['X-MS-Access-Token'] = graphResponse.accessToken
        } catch (graphError) {
          // Graph token is optional, continue without it
          console.debug('Graph token not available, continuing without manager sync')
        }
      } catch (error) {
        // If silent token acquisition fails, emit event for auth context to handle
        console.error('Failed to acquire token silently:', error)
        authEvents.emit('auth:sessionExpired', 'Token refresh failed')
      }
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiError>) => {
    if (error.response?.status === 401 && !isHandling401) {
      isHandling401 = true

      const message = (error.response?.data as any)?.error?.message || ''
      if (message.toLowerCase().includes('deactivated')) {
        authEvents.emit('auth:accountDeactivated', message)
      } else {
        authEvents.emit('auth:sessionExpired', 'Session expired or invalid')
      }

      setTimeout(() => {
        isHandling401 = false
      }, 1000)
    }

    // Format error for consistent handling
    const apiError: ApiError = error.response?.data || {
      error: {
        code: 'NETWORK_ERROR',
        message: error.message || 'An unexpected error occurred',
      },
    }

    return Promise.reject(apiError)
  }
)

export default apiClient
